import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { TurnSlots } from "../thread/slots";

/** `bar`, in the terminal register — the turn's attribution, drawn the way a
 * TUI draws a closing line: a dim `⎿` continuation rule with the metadata
 * joined by `·`, and any actions as bracketed key hints.
 *
 * This slot was the terminal skin's predicted failure. The native bar
 * discloses its metadata through an anchored tooltip, chosen precisely so a
 * row in a virtualized list never changes height; a terminal has no tooltip
 * and no hover, so if that property were part of the CONTRACT this slot could
 * not be skinned at all.
 *
 * It is not. The contract promises `meta` and `actions` — data — and says
 * nothing about how they are revealed. So the answer here is the opposite
 * one: the terminal bar DISPLACES, spending a line to say what the native
 * skin spends a hover on. That is a real difference in behaviour and the
 * right one for the register; a monospace transcript is already a column of
 * lines, and one more dim line reads as belonging.
 *
 * The finding, recorded because the taxonomy predicted otherwise: "metadata
 * that displaces nothing" is a property of the NATIVE skin, not of `bar`. */
@component({ name: "std-term-turn-bar" })
export class TermTurnBar extends LonaWebComponent {
  private $meta: HTMLElement = this.$("meta");
  private $actions: HTMLElement = this.$("actions");
  private $faces: HTMLElement = this.$("faces");

  /** Same alphabet as `turn-term/tool.ts`, on purpose: an operator reading
   * the pile and the rows it stands for should not be learning two sets of
   * marks for one idea. */
  static readonly MARK: Record<TurnSlots.ToolStatus, string> = {
    pending: "○",
    running: "◐",
    done: "⏺",
    failed: "✕",
    declined: "⊘",
  };

  static readonly TONE: Record<TurnSlots.ToolStatus, string> = {
    pending: "var(--text-ter, #8b93a7)",
    running: "var(--fg-blue, #3b82f6)",
    done: "var(--fg-green, #2da44e)",
    failed: "var(--fg-red, #e5484d)",
    declined: "var(--fg-yellow, #d4a72c)",
  };

  static readonly FACE_CAP = 24;

  static makeWith(props: TurnSlots.Props["bar"]): TermTurnBar {
    return (TermTurnBar.make() as TermTurnBar).bind(props);
  }

  bind(props: TurnSlots.Props["bar"]): this {
    const meta = props.meta ?? [];
    // `key=value`, `·`-joined: the same shape a status line uses, and it
    // survives truncation from the right without losing the leading facts.
    this.$meta.textContent = meta.map((entry) => `${entry.key} ${entry.value}`).join("  ·  ");
    this.$meta.style.display = meta.length > 0 ? "" : "none";

    const actions = props.actions ?? [];
    this.$actions.replaceChildren();
    for (const action of actions) {
      // A real button behind the terminal grammar: keyboard-reachable, and
      // activated through the gesture manager so a drag release over it
      // does not fire the action.
      const $action = document.createElement("button");
      $action.type = "button";
      $action.className = "action";
      // A terminal spells its affordance out: the label carries the key, so
      // there is no icon to decode and nothing to hover to discover.
      $action.textContent = `[${action.label}]`;
      GESTURE_MANAGER.addPointerEvent($action, {
        onClick: () => props.onAction?.(action.id),
      });
      this.$actions.append($action);
    }
    this.$actions.style.display = actions.length > 0 ? "" : "none";
    this.bindFaces(props);
    return this;
  }

  /** The same data as the native facepile, and deliberately not a pile.
   *
   * Overlapping circles are a pixel technique; a cell grid has no sub-cell
   * offset to overlap with, and faking one with half-width glyphs would break
   * the column the rest of this skin runs on. So the terminal answer is the
   * one a terminal already uses for a set of outcomes — the status glyphs in
   * a row, in order, in the same alphabet the tool rows use.
   *
   * That is the divergence this contract was built to allow: `faces` promises
   * a status and a subject, and says nothing about stacking. A `faces` that
   * had promised a pile could not have been rendered here at all. */
  private bindFaces(props: TurnSlots.Props["bar"]): void {
    const faces = props.faces ?? [];
    this.$faces.replaceChildren();
    this.$faces.style.display = faces.length > 0 ? "" : "none";
    if (faces.length === 0) return;
    const shown = faces.slice(0, TermTurnBar.FACE_CAP);
    for (const face of shown) {
      const $glyph = document.createElement("span");
      $glyph.className = "face";
      $glyph.textContent = TermTurnBar.MARK[face.tone];
      $glyph.style.color = TermTurnBar.TONE[face.tone];
      $glyph.title = face.label;
      this.$faces.append($glyph);
    }
    const rest = faces.length - shown.length;
    if (rest > 0) {
      const $rest = document.createElement("span");
      $rest.className = "face-rest";
      $rest.textContent = ` +${rest}`;
      this.$faces.append($rest);
    }
    // Bound once, reading the CURRENT callback at click time: `$faces`
    // survives rebinding because it comes from the template, so a listener
    // added per bind would stack and fire the modal N times on a pooled
    // row — and a stale closure would open the previous turn's faces.
    // Through the gesture manager, so a drag release here does not open it.
    if (!this.facesBound) {
      this.facesBound = true;
      GESTURE_MANAGER.addPointerEvent(this.$faces, {
        onClick: () => this.onFaces?.(),
      });
    }
    this.onFaces = props.onFaces ?? null;
  }

  private facesBound = false;
  private onFaces: Option<() => void> = null;

  static $styles = [
    css`
      :host {
        display: flex;
        align-items: baseline;
        gap: 8px;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--turn-term-size, 12px);
        line-height: var(--turn-term-leading, 1.7);
        color: var(--text-ter, #8b93a7);
      }
      /* The continuation glyph is the whole visual claim: this line belongs
         to the turn above it rather than starting a new one. */
      #rule {
        flex: none;
        color: var(--divider, #d8dee9);
      }
      #meta {
        white-space: pre;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      #actions {
        display: flex;
        gap: 6px;
      }
      .action {
        /* Button chrome reset: the affordance is the bracketed label. */
        appearance: none;
        background: none;
        border: none;
        padding: 0;
        margin: 0;
        font: inherit;
        cursor: pointer;
        color: var(--text-sec, #5c6370);
      }
      .action:focus-visible {
        outline: 1px solid var(--fg-accent, #4c8bf5);
        outline-offset: 1px;
      }
      .action:hover {
        color: var(--text-pri, #1f2328);
      }
      #faces {
        cursor: pointer;
        letter-spacing: 1px;
        white-space: nowrap;
      }
      #faces:hover {
        text-decoration: underline;
        text-underline-offset: 3px;
      }
      .face-rest {
        color: var(--text-ter, #8b93a7);
        letter-spacing: 0;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <span id="rule">⎿</span>
    <span id="meta"></span>
    <span id="actions"></span>
    <span id="faces"></span>
  `;
}
